import Nav from "~/components/nav";
import OptimizeLayout from "~/components/layout";
import Notice from "~/components/notice/home";
import { ExperimentProvider } from "~/state-containers/experiment";
import { Footer } from "~/components/footer";
import { FaqContainer } from "~/components/home/faq-container";
/* This example requires Tailwind CSS v2.0+ */
import { CheckIcon } from "@heroicons/react/outline";
import { Action } from "~/components/action";
import clsx from "clsx";
import { useApp } from "~/state-containers/app";
import { useEffect } from "react";
import { HeadContent } from "~/components/head";

const pricing = {
  tiers: [
    {
      title: "Free Trial",
      price: "7 days",
      frequency: " Free",
      description: "No payment info collected until AFTER your trial ends.",
      features: [
        "Try all Ladder training plans (TEAMS)",
        "Complete as many workouts as you’d like",
        "Explore all communities",
        "Chat 1-on-1 with your coach",
        "Get coach feedback on your form",
      ],
      bg: "linear-gradient(71.21deg, #FFFFFF 7.11%, #FFFFFF 59.21%, rgba(255, 255, 255, 0) 98.03%)",
    },
    {
      title: "PRO Plan",
      price: "$29.99",
      frequency: "/ month",
      description:
        "Planned workout programming and access to the Ladder community.",
      features: [
        "Join 1 Ladder team",
        "Change your team up to 1x per month",
        "5+ new workouts each week",
        "Access to community chats",
        "3 Save & Replay workouts",
      ],
      bg: "linear-gradient(71.21deg, #B5B4B4 7.11%, #F7F7F7 59.21%, rgba(176, 176, 176, 0) 98.03%)",
    },
    {
      title: "ELITE Plan",
      price: "$44.99",
      frequency: "/ month",
      description: "Planned workout programming and 1-on-1 Coach help.",
      features: [
        "Everything in PRO plan",
        "20 Save & Replay workouts",
        "Chat 1-on-1  with your coach",
        "2 form checks per month",
      ],
      bg: "linear-gradient(71.21deg, #1A1A1A 7.11%, #444444 59.21%, rgba(14, 14, 14, 0.76) 98.03%)",
      dark: true,
    },
    {
      title: "Upgrade with +",
      price: "+ $15",
      frequency: "/ month",
      description:
        "Everything Ladder has to offer. Both PRO and ELITE members are eligible.",
      features: [
        "Access to all Ladder teams and workouts",
        "Unlimited Save & Replay workouts",
        // "Unlimited workout downloads",
      ],
      bg: "linear-gradient(71.21deg, #E6FF00 7.11%, #F5FF9A 59.21%, #EEFF51 98.03%)",
    },
  ],
};

function PricingContainer() {
  return (
    <div className="relative w-full py-16 lg:py-32 bg-basic">
      <div className="px-4 mx-auto lg:max-w-screen-xl xl:max-w-screen-2xl">
        <div className="flex flex-col center">
          <h2 className="mb-4 text-3xl text-center text-black uppercase lg:max-w-6xl lg:px-12 lg:leading-tight lg:text-5xl heading">
            maximize your workout plan, without maxiNG OUT your wallet
          </h2>
          <p className="mx-auto mt-4 text-xl text-center text-gray-500 lg:px-24">
            Flexible membership plans that let you choose based on the features
            you care about most.
          </p>

          <Action
            at="pricing.cta"
            className="px-10 py-4 mt-10 text-white bg-black rounded-full cta"
          />
        </div>

        {/* Tiers */}
        <div className="mt-24 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-4 lg:gap-x-4 lg:gap-y-4 xl:gap-x-4">
          {pricing.tiers.map((tier) => (
            <div
              key={tier.title}
              className="relative flex flex-col p-8 border shadow-2xl rounded-2xl"
              style={{ background: tier.bg }}
            >
              <div className="flex-1">
                <h3
                  className={clsx("text-xl font-semibold heading", {
                    "text-white": tier.dark,
                    "text-black": !tier.dark,
                  })}
                >
                  {tier.title}
                </h3>
                <p
                  className={clsx("flex items-baseline mt-4 heading", {
                    "text-white": tier.dark,
                    "text-black": !tier.dark,
                  })}
                >
                  <span className="text-5xl font-extrabold tracking-tight">
                    {tier.price}
                  </span>
                  <span className="ml-1 text-lg font-semibold opacity-50">
                    {tier.frequency}
                  </span>
                </p>
                <p
                  className={clsx("mt-6", {
                    "text-white": tier.dark,
                    "text-black": !tier.dark,
                  })}
                >
                  {tier.description}
                </p>

                {/* Feature list */}
                <ul role="list" className="mt-6 space-y-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex">
                      <CheckIcon
                        className={clsx("flex-shrink-0 w-6 h-6", {
                          "text-volt": tier.dark,
                          "text-black": !tier.dark,
                        })}
                        aria-hidden="true"
                      />
                      <span
                        className={clsx("ml-3", {
                          "text-white": tier.dark,
                          "text-black": !tier.dark,
                        })}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { setIsHeroCTAInView } = useApp();
  useEffect(() => {
    setIsHeroCTAInView(false);
  }, []);

  return (
    <>
      <HeadContent />
      <ExperimentProvider>
        <Notice />
        <Nav showWWOD />
        <PricingContainer />
        <FaqContainer />
        <Footer />
      </ExperimentProvider>
    </>
  );
}

Pricing.Layout = OptimizeLayout;
